import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { selectUserRole } from "../../../store/authSlice";
import {
  appApi,
  useGetPrConfigQuery,
  useGetPrPeriodsQuery,
  useGetPrScoreboardQuery,
  useGetPrEmployeeQuery,
} from "../../../store/appApi";
import {
  createPrPeriodApi,
  generatePrPeriodApi,
  finalisePrPeriodApi,
  reopenPrPeriodApi,
  adjustPrEmployeeApi,
  waiveSundaysPrEmployeeApi,
  updatePrConfigApi,
} from "../../../services/performanceReview";

const panel =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const field =
  "rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";
const ghost =
  "rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:border-amber-400 hover:text-amber-700";
const primary =
  "rounded-lg bg-amber-500 px-4 py-2 text-xs font-semibold text-slate-950 transition hover:bg-amber-400 disabled:opacity-50";
const WHITE = { backgroundColor: "#fff", color: "#1f2937" };

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";
const errMsg = (e) => e?.response?.data?.message || "Something went wrong.";
const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const monthEnd = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
};

const STATUS_TONE = {
  draft: "bg-gray-100 text-gray-600",
  generated: "bg-cyan-50 text-cyan-700",
  finalised: "bg-emerald-50 text-emerald-700",
};

const PerformanceReview = () => {
  const dispatch = useDispatch();
  const role = useSelector(selectUserRole);
  const [view, setView] = useState("periods");
  const [periodId, setPeriodId] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const refresh = () =>
    dispatch(appApi.util.invalidateTags(["PerformanceReview"]));

  const { data: periodsData, isFetching: loadingPeriods } = useGetPrPeriodsQuery(
    undefined,
    { skip: role !== "super-admin" },
  );
  const periods = periodsData?.periods || [];

  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({
    label: new Date().toLocaleString("en-IN", { month: "long", year: "numeric" }),
    periodStart: monthStart(),
    periodEnd: monthEnd(),
  });

  if (role !== "super-admin") {
    return (
      <>
        <PageTitle activeMenu="Performance Review" motherMenu="Workforce" />
        <div className={`${panel} p-10 text-center`}>
          <h3 className="text-lg font-semibold text-gray-900">Super-admin only</h3>
        </div>
      </>
    );
  }

  const createPeriod = async (e) => {
    e.preventDefault();
    try {
      setBusy("create");
      await createPrPeriodApi(form);
      refresh();
      Swal.fire({ icon: "success", title: "Period created", timer: 1000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed", text: errMsg(err) });
    } finally {
      setBusy("");
    }
  };
  const act = async (fn, id, okTitle) => {
    try {
      setBusy(id);
      await fn(id);
      refresh();
      if (okTitle)
        Swal.fire({ icon: "success", title: okTitle, timer: 1000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed", text: errMsg(err) });
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <PageTitle activeMenu="Performance Review" motherMenu="Workforce" />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full bg-gray-100 p-1">
          {[
            ["periods", "Periods"],
            ["config", "Rule Config"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => {
                setView(k);
                setPeriodId(null);
                setEmployeeId(null);
              }}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                view === k || (k === "periods" && ["scoreboard", "employee"].includes(view))
                  ? "bg-white text-gray-900 shadow"
                  : "text-gray-500"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {["scoreboard", "employee"].includes(view) && (
          <button
            className={ghost}
            onClick={() =>
              setView(view === "employee" ? "scoreboard" : "periods")
            }
          >
            ← Back
          </button>
        )}
      </div>

      {view === "config" && <RuleConfig refresh={refresh} />}

      {view === "periods" && (
        <>
          <section className={`${panel} mb-6`}>
            <div className="border-b border-gray-100 px-5 py-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                New period
              </h2>
            </div>
            <form onSubmit={createPeriod} className="flex flex-wrap items-end gap-3 px-5 py-4">
              <label className="text-xs text-gray-500">
                Label
                <input
                  className={`${field} mt-0.5 block w-56`}
                  style={WHITE}
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                />
              </label>
              <label className="text-xs text-gray-500">
                From
                <input
                  type="date"
                  className={`${field} mt-0.5 block w-44`}
                  style={WHITE}
                  value={form.periodStart}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, periodStart: e.target.value }))
                  }
                />
              </label>
              <label className="text-xs text-gray-500">
                To
                <input
                  type="date"
                  className={`${field} mt-0.5 block w-44`}
                  style={WHITE}
                  value={form.periodEnd}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, periodEnd: e.target.value }))
                  }
                />
              </label>
              <button className={primary} disabled={busy === "create"}>
                {busy === "create" ? "…" : "Create"}
              </button>
            </form>
          </section>

          <section className={panel}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3 text-left">Period</th>
                    <th className="px-4 py-3 text-left">Range</th>
                    <th className="px-4 py-3 text-center">Employees</th>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loadingPeriods ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-gray-400">
                        Loading…
                      </td>
                    </tr>
                  ) : periods.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-gray-400">
                        No periods yet.
                      </td>
                    </tr>
                  ) : (
                    periods.map((p) => (
                      <tr key={p._id} className="hover:bg-amber-50/30">
                        <td className="px-4 py-2.5 font-medium text-gray-800">
                          {p.label}
                        </td>
                        <td className="px-4 py-2.5 text-gray-500">
                          {fmtDate(p.periodStart)} – {fmtDate(p.periodEnd)}
                        </td>
                        <td className="px-4 py-2.5 text-center tabular-nums">
                          {p.employeeCount || 0}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                              STATUS_TONE[p.status] || "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {p.status}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="inline-flex flex-wrap justify-end gap-1">
                            {p.status !== "draft" && (
                              <button
                                className={ghost}
                                onClick={() => {
                                  setPeriodId(p._id);
                                  setView("scoreboard");
                                }}
                              >
                                Open
                              </button>
                            )}
                            {p.status !== "finalised" && (
                              <button
                                className={ghost}
                                disabled={busy === p._id}
                                onClick={() =>
                                  act(generatePrPeriodApi, p._id, "Generated")
                                }
                              >
                                {busy === p._id ? "…" : "Generate"}
                              </button>
                            )}
                            {p.status === "generated" && (
                              <button
                                className={ghost}
                                onClick={() =>
                                  act(finalisePrPeriodApi, p._id, "Finalised")
                                }
                              >
                                Finalise
                              </button>
                            )}
                            {p.status === "finalised" && (
                              <button
                                className={ghost}
                                onClick={() =>
                                  act(reopenPrPeriodApi, p._id, "Reopened")
                                }
                              >
                                Reopen
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {view === "scoreboard" && periodId && (
        <Scoreboard
          periodId={periodId}
          onPick={(eid) => {
            setEmployeeId(eid);
            setView("employee");
          }}
        />
      )}

      {view === "employee" && periodId && employeeId && (
        <EmployeeCard
          periodId={periodId}
          employeeId={employeeId}
          refresh={refresh}
        />
      )}
    </>
  );
};

const Scoreboard = ({ periodId, onPick }) => {
  const { data, isFetching } = useGetPrScoreboardQuery({ id: periodId });
  const totals = data?.totals || [];
  const period = data?.period;
  return (
    <section className={panel}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-5 py-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-800">{period?.label}</h2>
          <p className="text-xs text-gray-400">
            {fmtDate(period?.periodStart)} – {fmtDate(period?.periodEnd)} ·{" "}
            {period?.status}
          </p>
        </div>
        <span className="text-xs text-gray-400">
          {period?.generatedAt ? `Generated ${fmtDate(period.generatedAt)}` : "Not generated"}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Employee</th>
              <th className="px-4 py-3 text-left">Streams</th>
              <th className="px-4 py-3 text-center">Auto</th>
              <th className="px-4 py-3 text-center">Manual</th>
              <th className="px-4 py-3 text-center">Net stars</th>
              <th className="px-4 py-3 text-center">Band</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isFetching ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : totals.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                  Nothing generated for this period yet.
                </td>
              </tr>
            ) : (
              totals.map((t, i) => (
                <tr
                  key={t.employeeId}
                  className="cursor-pointer hover:bg-amber-50/30"
                  onClick={() => onPick(t.employeeId)}
                >
                  <td className="px-4 py-2.5 tabular-nums text-gray-400">{i + 1}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-gray-800">{t.name}</div>
                    <div className="text-xs text-gray-400">{t.role}</div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">
                    {(t.streams || []).join(", ")}
                  </td>
                  <td className="px-4 py-2.5 text-center tabular-nums text-gray-600">
                    {t.autoStars ?? 0}
                  </td>
                  <td className="px-4 py-2.5 text-center tabular-nums text-gray-600">
                    {t.manualStars ?? 0}
                  </td>
                  <td
                    className={`px-4 py-2.5 text-center text-lg font-semibold tabular-nums ${
                      (t.netStars ?? 0) >= 0 ? "text-emerald-700" : "text-rose-600"
                    }`}
                  >
                    {t.netStars ?? 0}
                  </td>
                  <td className="px-4 py-2.5 text-center text-amber-600">
                    {t.band}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const EmployeeCard = ({ periodId, employeeId, refresh }) => {
  const { data, isFetching } = useGetPrEmployeeQuery({ id: periodId, employeeId });
  const { data: configData } = useGetPrConfigQuery();
  const [adj, setAdj] = useState({ stars: "", note: "", ruleKey: "" });
  const [busy, setBusy] = useState(false);
  const [sundayBusy, setSundayBusy] = useState(false);
  const [open, setOpen] = useState({});

  // Every configured rule parameter, grouped by category, for the "which
  // parameter does this adjustment relate to" dropdown — e.g. "Modem
  // recovered" or "Punches with a selfie". The Sunday waiver isn't in this
  // list: it's not a scoring rule, it has its own dedicated action below.
  const ruleCategories = useMemo(() => {
    const byCat = {};
    Object.entries(configData?.config?.rules || {}).forEach(([key, r]) => {
      const cat = r.category || "other";
      (byCat[cat] = byCat[cat] || []).push({ key, label: r.label || key });
    });
    Object.values(byCat).forEach((list) =>
      list.sort((a, b) => a.label.localeCompare(b.label)),
    );
    return byCat;
  }, [configData]);

  const save = async () => {
    if (!adj.stars || !adj.note.trim()) {
      Swal.fire({ icon: "warning", title: "Stars and a reason are required." });
      return;
    }
    try {
      setBusy(true);
      await adjustPrEmployeeApi(periodId, employeeId, {
        stars: Number(adj.stars),
        note: adj.note.trim(),
        ruleKey: adj.ruleKey || undefined,
      });
      setAdj({ stars: "", note: "", ruleKey: "" });
      refresh();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  // For employees whose weekly off is Sunday, penalties can still fire on
  // that day (there's no per-employee weekly-off setting, only the shared
  // holiday list). This waives every auto-generated Sunday penalty for this
  // employee in one action instead of adjusting each one manually. Safe to
  // run again — it replaces the previous waiver rather than stacking.
  const waiveSundays = async () => {
    const confirm = await Swal.fire({
      icon: "question",
      title: "Waive Sunday penalties?",
      text: `Offsets every auto-generated Sunday penalty for ${emp.name || "this employee"} in this period with a single manual adjustment.`,
      showCancelButton: true,
      confirmButtonText: "Waive",
    });
    if (!confirm.isConfirmed) return;
    try {
      setSundayBusy(true);
      const res = await waiveSundaysPrEmployeeApi(periodId, employeeId, {
        note: "Sunday penalty waived — Sunday is this employee's weekly off",
      });
      const waived = res?.data?.waived || 0;
      Swal.fire({
        icon: "success",
        title: waived ? `Waived +${waived} stars` : "Nothing to waive",
        text: waived
          ? undefined
          : "No Sunday penalties were found for this employee in the period.",
        timer: waived ? 1600 : undefined,
        showConfirmButton: !waived,
      });
      refresh();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: errMsg(e) });
    } finally {
      setSundayBusy(false);
    }
  };

  const emp = data?.employee || {};
  const breakdown = data?.breakdown || [];

  return (
    <section className={panel}>
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {emp.name || "Employee"}
        </h2>
        <p className="text-sm text-gray-500">
          {emp.role} · net{" "}
          <span
            className={`font-semibold ${
              (data?.netStars ?? 0) >= 0 ? "text-emerald-700" : "text-rose-600"
            }`}
          >
            {data?.netStars ?? 0}
          </span>{" "}
          stars · <span className="text-amber-600">{data?.band}</span>
        </p>
      </div>

      <div className="divide-y divide-gray-100">
        {isFetching ? (
          <div className="px-5 py-12 text-center text-gray-400">Loading…</div>
        ) : breakdown.length === 0 ? (
          <div className="px-5 py-12 text-center text-gray-400">
            No stars for this employee in the period.
          </div>
        ) : (
          breakdown.map((g) => (
            <div key={g.ruleKey} className="px-5 py-3">
              <button
                className="flex w-full items-center justify-between text-left"
                onClick={() => setOpen((o) => ({ ...o, [g.ruleKey]: !o[g.ruleKey] }))}
              >
                <span className="text-sm font-medium text-gray-800">
                  {open[g.ruleKey] ? "▾" : "▸"} {g.ruleLabel}
                  <span className="ml-2 text-xs text-gray-400">
                    {g.stream} · {g.count}×
                  </span>
                </span>
                <span
                  className={`text-sm font-semibold tabular-nums ${
                    g.stars >= 0 ? "text-emerald-700" : "text-rose-600"
                  }`}
                >
                  {g.stars > 0 ? `+${g.stars}` : g.stars}
                </span>
              </button>
              {open[g.ruleKey] && (
                <div className="mt-2 space-y-1 rounded-lg border border-gray-100 bg-gray-50 p-2 text-xs text-gray-500">
                  {g.items.map((it, i) => (
                    <div key={i} className="flex justify-between gap-3">
                      <span>
                        {fmtDate(it.at)} — {it.note || it.sourceType}
                      </span>
                      <span
                        className={
                          it.stars >= 0 ? "text-emerald-700" : "text-rose-600"
                        }
                      >
                        {it.stars > 0 ? `+${it.stars}` : it.stars}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2 border-t border-gray-100 px-5 py-4">
        <label className="text-xs text-gray-500">
          Manual adjust (± stars)
          <input
            className={`${field} mt-0.5 block w-28`}
            style={WHITE}
            value={adj.stars}
            onChange={(e) => setAdj((a) => ({ ...a, stars: e.target.value }))}
            placeholder="e.g. -3"
          />
        </label>
        <label className="min-w-[14rem] text-xs text-gray-500">
          Parameter (optional)
          <select
            className={`${field} mt-0.5 block w-full`}
            style={WHITE}
            value={adj.ruleKey}
            onChange={(e) => setAdj((a) => ({ ...a, ruleKey: e.target.value }))}
          >
            <option value="" style={WHITE}>
              General / other
            </option>
            {Object.entries(ruleCategories).map(([cat, list]) => (
              <optgroup key={cat} label={cat}>
                {list.map((r) => (
                  <option key={r.key} value={r.key} style={WHITE}>
                    {r.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <label className="min-w-[14rem] flex-1 text-xs text-gray-500">
          Reason (required)
          <input
            className={`${field} mt-0.5 block w-full`}
            style={WHITE}
            value={adj.note}
            onChange={(e) => setAdj((a) => ({ ...a, note: e.target.value }))}
          />
        </label>
        <button className={primary} disabled={busy} onClick={save}>
          {busy ? "…" : "Add adjustment"}
        </button>
        <button className={ghost} disabled={sundayBusy} onClick={waiveSundays}>
          {sundayBusy ? "…" : "Waive Sunday penalties"}
        </button>
      </div>
    </section>
  );
};

const RuleConfig = ({ refresh }) => {
  const { data } = useGetPrConfigQuery();
  const [rules, setRules] = useState(null);
  const [busy, setBusy] = useState(false);
  const cfg = rules || data?.config?.rules || {};

  // Kept as typed until Save, so "-" and "0." survive on the way to "-0.5".
  const setStar = (key, v) =>
    setRules({ ...cfg, [key]: { ...cfg[key], stars: v } });

  const save = async () => {
    try {
      setBusy(true);
      const payload = {};
      Object.entries(cfg).forEach(([k, r]) => {
        if (r.stars !== undefined) payload[k] = { stars: Number(r.stars) || 0 };
      });
      await updatePrConfigApi({ rules: payload });
      setRules(null);
      refresh();
      Swal.fire({ icon: "success", title: "Saved", timer: 900, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  const byCat = {};
  Object.entries(cfg).forEach(([k, r]) => {
    (byCat[r.category || "other"] = byCat[r.category || "other"] || []).push([k, r]);
  });

  return (
    <section className={panel}>
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          Rule star values
        </h2>
        <button className={primary} disabled={busy} onClick={save}>
          {busy ? "…" : "Save"}
        </button>
      </div>
      <div className="p-5">
        {Object.entries(byCat).map(([cat, list]) => (
          <div key={cat} className="mb-5">
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-amber-600">
              {cat}
            </h3>
            <div className="space-y-1.5">
              {list.map(([key, r]) => (
                <div
                  key={key}
                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2"
                >
                  <div>
                    <div className="text-sm text-gray-800">{r.label}</div>
                    <div className="text-[10px] text-gray-400">
                      {key}
                      {r.per ? ` · per ${r.per}` : ""}
                      {r.dayTiers ? " · tiered by days" : ""}
                      {r.tiers ? " · tiered by count" : ""}
                    </div>
                  </div>
                  {r.stars !== undefined ? (
                    <input
                      className={`${field} w-20 text-center`}
                      style={WHITE}
                      value={r.stars}
                      onChange={(e) => setStar(key, e.target.value)}
                    />
                  ) : (
                    <span className="text-[10px] text-gray-400">tier table</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default PerformanceReview;
